import React, { useEffect, useState, useRef } from 'react';
import { auth, db } from '../../../firebase/config';
import { doc, updateDoc, collection, getDocs, deleteDoc, addDoc } from 'firebase/firestore';
import { useAppSelector } from '../../../store';
import Button from '../../../components/Button';
import TextField from '../../../components/TextField';
import Toast from '../../../components/Toast';
import { FiPlus, FiSearch, FiEye, FiTrendingUp, FiEdit2, FiTrash2, FiUpload, FiDownload } from 'react-icons/fi';
import Icon from '../../../components/Icon';
import AddBranchModal from '../components/AddBranchModal';
import BranchDetailsModal from '../components/BranchDetailsModal';
import DeactivationModal from '../components/DeactivationModal';
import Modal from '../../../components/Modal';
import { fetchBranchesData, fetchBranchBankId } from '../service/branchesService';
import * as XLSX from 'xlsx';
import Table, { Column } from '../../../components/Table';

type Branch = {
    id: string;
    bankId: string;
    branchCode: string;
    address: string;
    mobile: string;
    locationId: string;
    securityCode: string;
    locationName: string;
    psoId?: string;
    psoName: string;
    psoEmail: string;
    psoContact: string;
    name?: string;
    branchManagerId?: string;
    operationalManagerId?: string;
    areaManagerId?: string;
    isActive?: boolean;
    deactivationReason?: string;
    deactivationDate?: string;
};

type Manager = {
    id: string;
    bankId: string;
    name: string;
    email: string;
    mobile: string;
    type?: string;
};

export default function Branches() {
    const [branches, setBranches] = useState<Branch[]>([]);
    const [filtered, setFiltered] = useState<Branch[]>([]);
    const [managers, setManagers] = useState<Record<string, Manager>>({});
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
        show: false,
        message: '',
        type: 'success',
    });
    const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeactivationModal, setShowDeactivationModal] = useState(false);
    const [branchToDeactivate, setBranchToDeactivate] = useState<Branch | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [branchToDelete, setBranchToDelete] = useState<Branch | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedFilter, setSelectedFilter] = useState<'all' | 'active' | 'inactive' | null>(null);
    const [bankId, setBankId] = useState<string | null>(null);
    const [psoFilter, setPsoFilter] = useState<string>('All');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [psos, setPsos] = useState<Array<{ id: string; name: string; psoId?: string; designation?: string }>>([]);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso'
    const isBankUser = userRole === 'bank';
    const pso = useAppSelector((s) => s.user.pso);

    useEffect(() => {
        if (!uid) return;

        const loadData = async () => {
            setLoading(true);
            try {
                let fetchedBankId: string | null = null;

                if (isBankUser) {
                    // For bank users, check if uid is a bank document ID or if there's a bank with userId == uid
                    const { getDoc, doc } = await import('firebase/firestore');
                    const { db } = await import('../../../firebase/config');
                    const directBank = await getDoc(doc(db, "banks", uid));
                    if (directBank.exists()) {
                        fetchedBankId = uid;
                    } else {
                        // Try query by userId field
                        const { getDocs, collection } = await import('firebase/firestore');
                        const banksSnap = await getDocs(collection(db, "banks"));
                        for (const bankDoc of banksSnap.docs) {
                            const bankData = bankDoc.data();
                            if (bankData.userId === uid) {
                                fetchedBankId = bankDoc.id;
                                break;
                            }
                        }
                    }
                } else {
                    fetchedBankId = await fetchBranchBankId(uid);
                }

                if (fetchedBankId) {
                    setBankId(fetchedBankId);
                    const { branches: branchesData, managers: managersData } = await fetchBranchesData(
                        uid,
                        isBankUser,
                        pso?.authUid,
                        pso?.designation
                    );
                    setBranches(branchesData);
                    setFiltered(branchesData);
                    setManagers(managersData);

                    // Fetch PSOs for filter dropdown (only for bank users)
                    if (isBankUser) {
                        try {
                            const psoSnap = await getDocs(collection(db, 'banks', fetchedBankId, 'pos'));
                            const psoList = psoSnap.docs
                                .map(d => ({
                                    id: d.id,
                                    ...d.data()
                                } as { id: string; name: string; psoId?: string; designation?: string }))
                                .filter(pso => {
                                    const designation = (pso.designation || '').toLowerCase();
                                    return designation === 'pso' || !designation;
                                });
                            setPsos(psoList);
                        } catch (err) {
                            console.error('Error fetching PSOs:', err);
                        }
                    }
                }
            } catch (err: any) {
                showToast('Failed to load branches data', 'error');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [uid, isBankUser, pso?.authUid, pso?.designation]);

    const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setToast({ show: true, message, type });
    };

    useEffect(() => {
        let list = branches;

        // Apply filter based on selected stat card
        if (selectedFilter === 'active') {
            list = list.filter(branch => branch.isActive !== false);
        } else if (selectedFilter === 'inactive') {
            list = list.filter(branch => branch.isActive === false);
        }

        // Apply PSO filter (only for bank users)
        if (isBankUser && psoFilter && psoFilter !== 'All') {
            if (psoFilter === 'No PSO') {
                list = list.filter(branch => {
                    if (!branch.psoId || branch.psoId === '') {
                        return true;
                    }
                    const psoExists = psos.some(pso => {
                        const psoValue = (pso as any).psoId || pso.id;
                        return psoValue === branch.psoId;
                    });
                    return !psoExists;
                });
            } else {
                list = list.filter(branch => branch.psoId === psoFilter);
            }
        }

        // Apply search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(branch => {
                const branchCode = (branch.branchCode || '').toString().toLowerCase();
                const address = (branch.address || '').toString().toLowerCase();
                return branchCode.includes(term) || address.includes(term);
            });
        }

        // Apply Date range filter
        if (startDate || endDate) {
            list = list.filter(branch => {
                if (!(branch as any).createdAt) return false;
                const createdAt = (branch as any).createdAt.toDate ? (branch as any).createdAt.toDate() : new Date((branch as any).createdAt);

                if (startDate && endDate) {
                    const start = new Date(startDate);
                    start.setHours(0, 0, 0, 0);
                    const end = new Date(endDate);
                    end.setHours(23, 59, 59, 999);
                    return createdAt >= start && createdAt <= end;
                } else if (startDate) {
                    const start = new Date(startDate);
                    start.setHours(0, 0, 0, 0);
                    return createdAt >= start;
                } else if (endDate) {
                    const end = new Date(endDate);
                    end.setHours(23, 59, 59, 999);
                    return createdAt <= end;
                }
                return true;
            });
        }

        // Sort by branch code alphabetically for all cases
        list.sort((a, b) => {
            const codeA = (a.branchCode || '').toString();
            const codeB = (b.branchCode || '').toString();
            return codeA.localeCompare(codeB);
        });

        setFiltered(list);
        setCurrentPage(1);
    }, [searchTerm, branches, selectedFilter, psoFilter, isBankUser, psos, startDate, endDate]);

    const handleReset = () => {
        setSearchTerm('');
        setSelectedFilter(null);
        setPsoFilter('All');
        setStartDate('');
        setEndDate('');
        setFiltered(branches);
        setCurrentPage(1);
    };

    const handleStatCardClick = (filter: 'all' | 'active' | 'inactive') => {
        setSelectedFilter(selectedFilter === filter ? null : filter);
        setCurrentPage(1);
    };

    const openDetailsModal = (branch: Branch, inEditMode: boolean = false) => {
        setSelectedBranch(branch);
        setEditMode(inEditMode);
        setShowDetailsModal(true);
    };

    const handleBranchAdded = async () => {
        if (!uid) return;
        setLoading(true);
        try {
            const { branches: branchesData, managers: managersData } = await fetchBranchesData(
                uid,
                isBankUser,
                pso?.authUid,
                pso?.designation
            );
            setBranches(branchesData);
            setFiltered(branchesData);
            setManagers(managersData);
            setCurrentPage(1);
            showToast('Branch added successfully', 'success');
            setShowAddModal(false);
        } catch (err: any) {
            console.error('Error refreshing branches:', err);
            showToast('Failed to refresh branches', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusToggle = (branch: Branch) => {
        if (branch.isActive !== false) {
            setBranchToDeactivate(branch);
            setShowDeactivationModal(true);
        } else {
            handleReactivate(branch);
        }
    };

    const handleDeactivate = async (reason: string, date: string) => {
        if (!branchToDeactivate || !uid) return;

        setLoading(true);
        try {
            const branchRef = doc(db, 'banks', branchToDeactivate.bankId, 'branch', branchToDeactivate.id);
            await updateDoc(branchRef, {
                isActive: false,
                deactivationReason: reason,
                deactivationDate: date,
            });

            const { branches: branchesData, managers: managersData } = await fetchBranchesData(
                uid,
                isBankUser,
                pso?.authUid,
                pso?.designation
            );
            setBranches(branchesData);
            setFiltered(branchesData);
            setManagers(managersData);

            setShowDeactivationModal(false);
            setBranchToDeactivate(null);
            showToast('Branch deactivated successfully', 'success');
        } catch (err: any) {
            console.error('Error deactivating branch:', err);
            showToast('Failed to deactivate branch', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleReactivate = async (branch: Branch) => {
        if (!uid) return;

        setLoading(true);
        try {
            const branchRef = doc(db, 'banks', branch.bankId, 'branch', branch.id);
            await updateDoc(branchRef, {
                isActive: true,
                deactivationReason: null,
                deactivationDate: null,
            });

            const { branches: branchesData, managers: managersData } = await fetchBranchesData(
                uid,
                isBankUser,
                pso?.authUid,
                pso?.designation
            );
            setBranches(branchesData);
            setFiltered(branchesData);
            setManagers(managersData);

            showToast('Branch reactivated successfully', 'success');
        } catch (err: any) {
            console.error('Error reactivating branch:', err);
            showToast('Failed to reactivate branch', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (branch: Branch) => {
        if (!isBankUser) return;
        setBranchToDelete(branch);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!branchToDelete || !bankId) return;

        setDeleting(true);
        try {
            await deleteDoc(doc(db, 'banks', bankId, 'branch', branchToDelete.id));

            const currentUid = uid || auth.currentUser?.uid || '';
            const { branches: branchesData, managers: managersData } = await fetchBranchesData(
                currentUid,
                isBankUser,
                pso?.authUid,
                pso?.designation
            );
            setBranches(branchesData);
            setFiltered(branchesData);
            setManagers(managersData);
            showToast('Branch deleted successfully', 'success');
        } catch (err: any) {
            console.error('Error deleting branch:', err);
            showToast('Failed to delete branch', 'error');
        } finally {
            setDeleting(false);
            setShowDeleteModal(false);
            setBranchToDelete(null);
        }
    };

    const handleImportClick = () => {
        if (!isBankUser) return;
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !bankId) return;

        setImporting(true);
        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet, { defval: '' });

            if (!rows.length) {
                showToast('No data found in the Excel file', 'warning');
                return;
            }

            let successCount = 0;
            let errorCount = 0;

            for (const row of rows) {
                try {
                    const branchCode = String(row['Branch Code'] || '').trim();
                    const address = String(row['Address'] || '').trim();
                    if (!branchCode || !address) {
                        throw new Error('Branch Code and Address are required');
                    }

                    const mobile = String(row['Mobile'] || '').trim();
                    const securityCode = String(row['Client Code'] || row['Security Code'] || '').trim();
                    const locationId = String(row['Location ID'] || '').trim();
                    const psoIdentifier = String(row['PSO ID'] || '').trim();

                    const newBranch: Record<string, any> = {
                        branchCode,
                        address,
                        mobile,
                        locationId,
                        securityCode,
                        isActive: true,
                        createdAt: new Date(),
                    };

                    if (psoIdentifier) {
                        newBranch.psoId = psoIdentifier;
                    }

                    await addDoc(collection(db, 'banks', bankId, 'branch'), newBranch);
                    successCount += 1;
                } catch (error) {
                    console.error('Failed to import branch row:', error);
                    errorCount += 1;
                }
            }

            const { branches: branchesData, managers: managersData } = await fetchBranchesData(
                uid || auth.currentUser?.uid || '',
                isBankUser,
                pso?.authUid,
                pso?.designation
            );
            setBranches(branchesData);
            setFiltered(branchesData);
            setManagers(managersData);

            showToast(`Import completed: ${successCount} success, ${errorCount} failed`, errorCount ? 'warning' : 'success');
        } catch (error) {
            console.error('Error importing branches:', error);
            showToast('Failed to import branches', 'error');
        } finally {
            setImporting(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleExport = () => {
        const exportData = filtered.map(branch => ({
            'Branch Code': branch.branchCode,
            'Address': branch.address,
            'Mobile': branch.mobile,
            'Location': branch.locationName,
            'Location ID': branch.locationId,
            'Client Code': branch.securityCode,
            'PSO Name': branch.psoName || 'No PSO',
            'PSO ID': branch.psoId || '',
            'Status': branch.isActive !== false ? 'Active' : 'Inactive',
        }));

        if (!exportData.length) {
            showToast('No data to export', 'info');
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Branches');
        XLSX.writeFile(workbook, `branches_${new Date().toISOString().split('T')[0]}.xlsx`);
        showToast('Exported filtered data successfully', 'success');
    };

    const handleDownloadSample = () => {
        const sampleData = [{
            'Branch Code': '1234',
            'Address': 'Sample Branch Address',
            'Mobile': '03001234567',
            'Location ID': 'locationRefId',
            'Client Code': 'ABC-123',
            'PSO ID': 'psoAuthUid (optional)',
        }];

        const worksheet = XLSX.utils.json_to_sheet(sampleData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sample');
        XLSX.writeFile(workbook, 'branch_import_sample.xlsx');
        showToast('Sample file downloaded', 'success');
    };

    const totalPages = Math.ceil(filtered.length / perPage);
    const paginatedData = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

    // Calculate branch statistics
    const totalBranches = branches.length;
    const activeBranches = branches.filter(b => b.isActive !== false).length;
    const inactiveBranches = branches.filter(b => b.isActive === false).length;

    // Calculate percentages for graphs
    const activePercentage = totalBranches > 0 ? (activeBranches / totalBranches) * 100 : 0;
    const inactivePercentage = totalBranches > 0 ? (inactiveBranches / totalBranches) * 100 : 0;

    const columns: Column<Branch>[] = [
        {
            header: 'Branch Code',
            accessor: 'branchCode',
            width: '10%',
            render: (branch) => <span className="font-medium text-gray-900 dark:text-gray-100">{branch.branchCode}</span>
        },
        {
            header: 'Address',
            accessor: 'address',
            width: '25%',
            render: (branch) => (
                <div
                    style={{
                        maxWidth: '250px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal',
                    }}
                    className="text-gray-600 dark:text-gray-400"
                >
                    {branch.address}
                </div>
            )
        },
        {
            header: 'Mobile',
            accessor: 'mobile',
            width: '12%',
            render: (branch) => <span className="text-gray-600 dark:text-gray-400">{branch.mobile}</span>
        },
        {
            header: 'Location',
            accessor: 'locationName',
            width: '15%',
            render: (branch) => <span className="text-gray-600 dark:text-gray-400">{branch.locationName}</span>
        },
        {
            header: 'Client Code',
            accessor: 'securityCode',
            width: '10%',
            render: (branch) => (
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                    {branch.securityCode}
                </span>
            )
        }
    ];

    if (isBankUser) {
        columns.push({
            header: 'PSO',
            width: '15%',
            render: (branch) => (
                <span className="text-gray-600 dark:text-gray-400">
                    {branch.psoName && branch.psoName !== 'N/A' ? branch.psoName : 'No PSO'}
                </span>
            )
        });
    }

    columns.push({
        header: 'Status',
        align: 'center',
        width: '10%',
        render: (branch) => (
            isBankUser ? (
                <div className="flex flex-col gap-2">
                    <label className="relative inline-flex cursor-pointer items-center">
                        <input
                            type="checkbox"
                            checked={branch.isActive !== false}
                            onChange={() => handleStatusToggle(branch)}
                            className="peer sr-only"
                            disabled={loading}
                        />
                        <div className="peer h-6 w-11 rounded-full bg-gray-200 transition-colors duration-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-blue-800"></div>
                    </label>
                    {branch.isActive === false && branch.deactivationDate && (
                        <div className="flex flex-col text-left">
                            <span className="text-[10px] text-red-500">
                                {branch.deactivationReason}
                            </span>
                            <span className="text-[10px] text-gray-400">
                                {new Date(branch.deactivationDate).toLocaleDateString()}
                            </span>
                        </div>
                    )}
                </div>
            ) : (
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${branch.isActive !== false
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    }`}>
                    {branch.isActive !== false ? 'Active' : 'Inactive'}
                </span>
            )
        )
    });

    columns.push({
        header: 'Action',
        width: '10%',
        render: (branch) => (
            <div className="flex items-center gap-2">
                <button
                    onClick={() => openDetailsModal(branch)}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 dark:focus:ring-blue-400"
                    title="View Details"
                >
                    <Icon icon={FiEye} className="h-4 w-4" />
                </button>
                {isBankUser && (
                    <>
                        <button
                            onClick={() => openDetailsModal(branch, true)}
                            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 dark:focus:ring-blue-400"
                            title="Edit"
                        >
                            <Icon icon={FiEdit2} className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => handleDeleteClick(branch)}
                            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-700 transition-all hover:border-red-500 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-400 dark:focus:ring-red-400"
                            title="Delete"
                        >
                            <Icon icon={FiTrash2} className="h-4 w-4" />
                        </button>
                    </>
                )}
            </div>
        )
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Loading branches...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-6">
                {/* Statistics Cards */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {/* Total Branches Card */}
                    <div className="rounded-xl border-2 border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-black/30">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Branches</p>
                                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{totalBranches}</p>
                            </div>
                            <div className="rounded-full bg-gray-200 p-3 dark:bg-gray-700">
                                <Icon icon={FiTrendingUp} className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                            </div>
                        </div>
                        {/* Mini Graph */}
                        <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                            <div className="h-full w-full bg-gray-500 dark:bg-gray-500"></div>
                        </div>
                    </div>

                    {/* Active Branches Card */}
                    <div
                        onClick={() => handleStatCardClick('active')}
                        className={`rounded-xl border-2 cursor-pointer transition-all p-6 shadow-sm ${selectedFilter === 'active'
                            ? 'border-gray-900 bg-gray-100 dark:border-gray-100 dark:bg-gray-800'
                            : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-black/30 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Branches</p>
                                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{activeBranches}</p>
                                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{activePercentage.toFixed(1)}% of total</p>
                            </div>
                            <div className="rounded-full bg-gray-200 p-3 dark:bg-gray-700">
                                <Icon icon={FiTrendingUp} className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                            </div>
                        </div>
                        {/* Mini Graph */}
                        <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                            <div
                                className="h-full bg-gray-500 dark:bg-gray-500 transition-all duration-500"
                                style={{ width: `${activePercentage}%` }}
                            ></div>
                        </div>
                    </div>

                    {/* Inactive Branches Card */}
                    <div
                        onClick={() => handleStatCardClick('inactive')}
                        className={`rounded-xl border-2 cursor-pointer transition-all p-6 shadow-sm ${selectedFilter === 'inactive'
                            ? 'border-gray-900 bg-gray-100 dark:border-gray-100 dark:bg-gray-800'
                            : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-black/30 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Inactive Branches</p>
                                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{inactiveBranches}</p>
                                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{inactivePercentage.toFixed(1)}% of total</p>
                            </div>
                            <div className="rounded-full bg-gray-200 p-3 dark:bg-gray-700">
                                <Icon icon={FiTrendingUp} className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                            </div>
                        </div>
                        {/* Mini Graph */}
                        <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                            <div
                                className="h-full bg-gray-500 dark:bg-gray-500 transition-all duration-500"
                                style={{ width: `${inactivePercentage}%` }}
                            ></div>
                        </div>
                    </div>
                </div>

                <Table
                    data={paginatedData}
                    columns={columns}
                    keyExtractor={(item) => item.id}
                    loading={loading}
                    emptyMessage={searchTerm ? 'No branches found matching your search' : 'No branches available'}
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage
                    }}
                    title="Branches"
                    description="Manage and view all branch information"
                    headerActions={
                        isBankUser && (
                            <div className="flex flex-wrap gap-3">
                                <input
                                    type="file"
                                    accept=".xlsx,.xls"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    className="hidden"
                                />
                                <Button
                                    variant="secondary"
                                    onClick={handleDownloadSample}
                                    className="flex items-center gap-2"
                                >
                                    <Icon icon={FiDownload} className="h-4 w-4" />
                                    Sample
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={handleImportClick}
                                    className="flex items-center gap-2"
                                    disabled={importing}
                                >
                                    <Icon icon={FiUpload} className="h-4 w-4" />
                                    {importing ? 'Importing...' : 'Import'}
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={handleExport}
                                    className="flex items-center gap-2"
                                >
                                    <Icon icon={FiDownload} className="h-4 w-4" />
                                    Export
                                </Button>
                                <Button
                                    onClick={() => setShowAddModal(true)}
                                    className="flex items-center gap-2"
                                >
                                    <Icon icon={FiPlus} className="h-4 w-4" />
                                    Add Branch
                                </Button>
                            </div>
                        )
                    }
                    filters={
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                            <div className="relative flex-1">
                                <Icon
                                    icon={FiSearch}
                                    className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                                />
                                <input
                                    type="text"
                                    placeholder="Search by Branch Code or Address..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                />
                            </div>
                            {isBankUser && (
                                <div className="relative">
                                    <select
                                        value={psoFilter}
                                        onChange={(e) => setPsoFilter(e.target.value)}
                                        className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-4 pr-10 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                    >
                                        <option value="All">All PSOs</option>
                                        <option value="No PSO">No PSO</option>
                                        {psos.map((pso) => {
                                            const psoValue = (pso as any).psoId || pso.id;
                                            return (
                                                <option key={pso.id} value={psoValue}>
                                                    {pso.name}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            )}

                            <div className="flex items-center gap-2">
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="rounded-xl border border-gray-300 bg-white py-2.5 px-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                    placeholder="Start Date"
                                />
                                <span className="text-gray-400">to</span>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="rounded-xl border border-gray-300 bg-white py-2.5 px-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                    placeholder="End Date"
                                />
                            </div>

                            {(searchTerm || (isBankUser && psoFilter !== 'All') || startDate || endDate) && (
                                <Button
                                    variant="secondary"
                                    onClick={handleReset}
                                    className="text-sm whitespace-nowrap"
                                >
                                    Clear Filters
                                </Button>
                            )}
                        </div>
                    }
                />
            </div>

            {/* Toast */}
            {toast.show && (
                <div className="pointer-events-none fixed right-4 top-4 z-50">
                    <div className="pointer-events-auto">
                        <Toast
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToast({ ...toast, show: false })}
                        />
                    </div>
                </div>
            )}

            {/* Modals */}
            <AddBranchModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={handleBranchAdded}
                bankId={bankId || undefined}
            />

            <BranchDetailsModal
                isOpen={showDetailsModal}
                onClose={() => {
                    setShowDetailsModal(false);
                    setEditMode(false);
                }}
                branch={selectedBranch}
                editMode={editMode}
                managers={managers}
                bankId={bankId || undefined}
                onUpdate={handleBranchAdded}
            />

            <DeactivationModal
                isOpen={showDeactivationModal}
                onClose={() => {
                    setShowDeactivationModal(false);
                    setBranchToDeactivate(null);
                }}
                onConfirm={handleDeactivate}
                title="Deactivate Branch"
                message={`Are you sure you want to deactivate branch ${branchToDeactivate?.branchCode}? This will prevent any operations on this branch.`}
            />

            <Modal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setBranchToDelete(null);
                }}
                title="Delete Branch"
            >
                <div>
                    <p className="mb-4 text-gray-600 dark:text-gray-400">
                        Are you sure you want to delete branch <strong>{branchToDelete?.branchCode}</strong>? This action cannot be undone.
                    </p>
                    <div className="flex justify-end gap-3">
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setShowDeleteModal(false);
                                setBranchToDelete(null);
                            }}
                            disabled={deleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleDeleteConfirm}
                            disabled={deleting}
                        >
                            {deleting ? 'Deleting...' : 'Delete'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
}
